/**
 * Kit Selector JavaScript - Refactored
 * Handles interactive multi-step variant selection for bundle/kit products
 * Optimized for performance and maintainability
 */

(function() {
  'use strict';

  // Utility functions
  const Utils = {
    generateUniqueId: (prefix, slideIndex, productId, value) => {
      return `${prefix}-${slideIndex}-${productId}-${value}`.replace(/\s+/g, '-');
    },

    findLabelForInput: (slide, input) => {
      let label = slide.querySelector(`label[for="${input.id}"]`);
      if (!label) {
        label = input.nextElementSibling;
        if (!label || !label.classList.contains('block-swatch')) {
          const allLabels = slide.querySelectorAll('label.block-swatch');
          for (const lbl of allLabels) {
            if (lbl.getAttribute('for') === input.id) {
              label = lbl;
              break;
            }
          }
        }
      }
      return label;
    },

    sortSizes: (sizes) => {
      const sizeOrder = ['PP', 'P', 'M', 'G', 'GG', 'EXG', '3G', 'XS', 'S', 'L', 'XL', 'XXL', '2XL', '3XL', 'EXGG'];
      return sizes.sort((a, b) => {
        const indexA = sizeOrder.indexOf(a[0]);
        const indexB = sizeOrder.indexOf(b[0]);

        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return a[0].localeCompare(b[0]);
      });
    }
  };

  // State management with observer pattern and product memory
  class KitState {
    constructor() {
      this.selections = new Map();
      this.observers = new Set();
      // Product memory for better UX with repeated products
      this.productMemory = new Map(); // productId -> last selection
      this.slideToProduct = new Map(); // slideIndex -> productId
      this.productToSlides = new Map(); // productId -> [slideIndexes]
      // Global variant usage counter for accurate stock tracking
      this.variantSelectionCount = new Map(); // variantId -> count

      console.log('[STOCK-DEBUG] 🚀 Kit State initialized with global variant counter');
    }

    addObserver(observer) {
      this.observers.add(observer);
    }

    removeObserver(observer) {
      this.observers.delete(observer);
    }

    notifyObservers(action, slideIndex, selection) {
      this.observers.forEach(observer => {
        if (typeof observer === 'function') {
          observer(action, slideIndex, selection);
        }
      });
    }

    setSelection(slideIndex, selection) {
      const previousSelection = this.selections.get(slideIndex);

      console.log(`[STATE-DEBUG] 📝 SETTING SELECTION for slide ${slideIndex}:`, {
        previous: previousSelection,
        new: selection,
        isComplete: selection && selection.variantId,
        timestamp: Date.now()
      });

      // Update global variant counter
      // Decrement counter for previous selection if it exists
      if (previousSelection?.variantId) {
        const prevVariantId = String(previousSelection.variantId);
        const prevCount = this.variantSelectionCount.get(prevVariantId) || 0;
        const newCount = Math.max(0, prevCount - 1);

        if (newCount === 0) {
          this.variantSelectionCount.delete(prevVariantId);
        } else {
          this.variantSelectionCount.set(prevVariantId, newCount);
        }

        console.log(`[STOCK-DEBUG] 📉 Decremented variant ${prevVariantId} usage: ${prevCount} -> ${newCount}`);
      }

      // Increment counter for new selection if it has a variantId
      if (selection?.variantId) {
        const variantId = String(selection.variantId);
        const currentCount = this.variantSelectionCount.get(variantId) || 0;
        const newCount = currentCount + 1;
        this.variantSelectionCount.set(variantId, newCount);

        console.log(`[STOCK-DEBUG] 📈 Incremented variant ${variantId} usage: ${currentCount} -> ${newCount}`);
      }

      this.selections.set(slideIndex, selection);

      // Update product memory if complete selection
      if (selection && selection.productId && selection.variantId) {
        const memoryUpdate = {
          color: selection.color,
          size: selection.size,
          variantId: selection.variantId,
          timestamp: Date.now()
        };

        console.log(`[STATE-DEBUG] 🧠 UPDATING PRODUCT MEMORY for product ${selection.productId}:`, memoryUpdate);
        this.productMemory.set(selection.productId, memoryUpdate);
      }

      this.notifyObservers('set', slideIndex, selection);
    }

    getSelection(slideIndex) {
      return this.selections.get(slideIndex);
    }

    hasSelection(slideIndex) {
      return this.selections.has(slideIndex);
    }

    clearSelection(slideIndex) {
      const selection = this.selections.get(slideIndex);

      // Decrement counter if selection had a variantId
      if (selection?.variantId) {
        const variantId = String(selection.variantId);
        const currentCount = this.variantSelectionCount.get(variantId) || 0;
        const newCount = Math.max(0, currentCount - 1);

        if (newCount === 0) {
          this.variantSelectionCount.delete(variantId);
        } else {
          this.variantSelectionCount.set(variantId, newCount);
        }

        console.log(`[STOCK-DEBUG] 🗑️ Cleared selection - Variant ${variantId} usage: ${currentCount} -> ${newCount}`);
      }

      this.selections.delete(slideIndex);
      this.notifyObservers('clear', slideIndex, null);
    }

    getAllSelections() {
      return Array.from(this.selections.entries());
    }

    hasCompleteSelection(slideIndex) {
      const selection = this.getSelection(slideIndex);
      return selection && selection.variantId;
    }

    // Register product-slide relationship
    registerProductSlide(productId, slideIndex) {
      this.slideToProduct.set(slideIndex, productId);

      if (!this.productToSlides.has(productId)) {
        this.productToSlides.set(productId, []);
      }
      if (!this.productToSlides.get(productId).includes(slideIndex)) {
        this.productToSlides.get(productId).push(slideIndex);
      }
    }

    // Get remembered selection for a product
    getProductMemory(productId) {
      return this.productMemory.get(productId);
    }

    // Check if product has been selected in any previous slide
    hasProductBeenSelected(productId, currentSlideIndex) {
      const slides = this.productToSlides.get(productId) || [];
      return slides.some(slideIdx => slideIdx < currentSlideIndex && this.hasCompleteSelection(slideIdx));
    }

    // Get count of how many times a variant has been selected globally
    // Uses the global counter for O(1) performance and accurate tracking
    getVariantUsageCount(variantId) {
      const normalizedVariantId = String(variantId);
      const count = this.variantSelectionCount.get(normalizedVariantId) || 0;

      console.log(`[STOCK-DEBUG] 📊 Getting usage count for variant ${normalizedVariantId}: ${count} time(s) selected`);

      return count;
    }

    // Get available stock for a variant from Liquid-injected inventory data
    getAvailableStock(variantId) {
      const normalizedVariantId = String(variantId);

      // Check if inventory data was loaded from Liquid
      if (!window.KIT_INVENTORY_DATA) {
        console.warn(`[STOCK-DEBUG] ⚠️ KIT_INVENTORY_DATA not found - inventory tracking unavailable`);
        return 0;
      }

      // Search through all products for this variant
      for (const productId in window.KIT_INVENTORY_DATA) {
        const product = window.KIT_INVENTORY_DATA[productId];
        if (product[normalizedVariantId]) {
          const inventoryQty = product[normalizedVariantId].inventory_quantity || 0;
          const isAvailable = product[normalizedVariantId].available;

          console.log(`[STOCK-DEBUG] 📦 Variant ${normalizedVariantId}: ${inventoryQty} units available (available flag: ${isAvailable})`);

          // Return 0 if not available, even if inventory_quantity shows stock
          return isAvailable ? inventoryQty : 0;
        }
      }

      console.warn(`[STOCK-DEBUG] ⚠️ Variant ${normalizedVariantId} not found in inventory data`);
      return 0;
    }

    // Get actual remaining stock after accounting for selections in other slides
    getActualAvailableQuantity(variantId) {
      const totalStock = this.getAvailableStock(variantId);
      const usageCount = this.getVariantUsageCount(variantId);
      const remaining = Math.max(0, totalStock - usageCount);

      console.log(`[STOCK-CALC] 🧮 Variant ${variantId}: ${totalStock} total - ${usageCount} used = ${remaining} remaining`);

      return remaining;
    }

    // Check if variant is available based on Liquid data
    isVariantAvailable(variantId) {
      const normalizedVariantId = String(variantId);

      if (!window.KIT_INVENTORY_DATA) {
        return false;
      }

      for (const productId in window.KIT_INVENTORY_DATA) {
        const product = window.KIT_INVENTORY_DATA[productId];
        if (product[normalizedVariantId]) {
          return product[normalizedVariantId].available === true;
        }
      }

      return false;
    }
  }

  // UI state manager
  class UIManager {
    constructor(wrapper, state) {
      this.wrapper = wrapper;
      this.state = state;
      this.elements = this.cacheElements();
    }

    cacheElements() {
      return {
        nextBtn: this.wrapper.querySelector('#kit-next-item'),
        prevBtn: this.wrapper.querySelector('#kit-prev-item'),
        addToCartBtn: this.wrapper.querySelector('#add-all-kit-to-cart'),
        stepCurrent: this.wrapper.querySelector('.step-current'),
        stepTotal: this.wrapper.querySelector('.step-total'),
        itemNumber: this.wrapper.querySelector('.item-number'),
        instructionText: this.wrapper.querySelector('.instruction-text')
      };
    }

    updateColorSelection(slide, selectedInput) {
      const allColorInputs = slide.querySelectorAll(`input[name="${selectedInput.name}"]`);

      allColorInputs.forEach(input => {
        const label = Utils.findLabelForInput(slide, input);
        if (label && label.classList.contains('block-swatch')) {
          label.classList.toggle('is-selected', input.checked);
        }
      });

      // Update color text
      const colorLabel = slide.querySelector('fieldset[data-option-type="color"] .selected-value');
      if (colorLabel) {
        colorLabel.textContent = selectedInput.dataset.colorName || selectedInput.value;
      }
    }

    updateSizeSelection(slide, selectedInput) {
      const allSizeInputs = slide.querySelectorAll(`input[name="${selectedInput.name}"]`);

      allSizeInputs.forEach(input => {
        const label = Utils.findLabelForInput(slide, input);
        if (label && label.classList.contains('block-swatch')) {
          label.classList.toggle('is-selected', input === selectedInput || input.checked);
        }
      });

      // Update size text
      const sizeLabel = slide.querySelector('fieldset[data-option-type="size"] .selected-size');
      if (sizeLabel) {
        sizeLabel.textContent = selectedInput.dataset.sizeName || selectedInput.value;
      }
    }

    updateProductImage(slide, imageUrl) {
      const productImage = slide.querySelector('.kit-product-image');
      if (productImage && imageUrl) {
        productImage.src = imageUrl;
      }
    }

    updateStepIndicator(currentSlideIndex, totalSlides, currentSlide) {
      const currentStep = currentSlideIndex + 1;

      if (this.elements.stepCurrent) this.elements.stepCurrent.textContent = currentStep;
      if (this.elements.stepTotal) this.elements.stepTotal.textContent = totalSlides;
      if (this.elements.itemNumber) this.elements.itemNumber.textContent = currentStep;

      if (this.elements.instructionText) {
        this.elements.instructionText.innerHTML = this.getInstructionText(currentSlide, currentStep, totalSlides);
      }
    }

    getInstructionText(slide, currentStep, totalSteps) {
      if (currentStep === totalSteps && totalSteps > 1) {
        return `Finalize as opções do <span class="item-number">último item</span>`;
      }

      const singleVariant = slide.querySelector('.single-variant-selector');
      if (singleVariant) {
        const available = singleVariant.dataset.available === 'true';
        return available
          ? `Item <span class="item-number">${currentStep}</span> - Pronto para adicionar`
          : `Item <span class="item-number">${currentStep}</span> - Produto indisponível`;
      }

      const hasColorOptions = slide.querySelector('fieldset[data-option-type="color"]');
      const hasSizeOptions = slide.querySelector('fieldset[data-option-type="size"]');

      if (hasColorOptions && hasSizeOptions) {
        return `Selecione a cor e o tamanho do item <span class="item-number">${currentStep}</span>`;
      } else if (hasColorOptions) {
        return `Selecione a cor do item <span class="item-number">${currentStep}</span>`;
      } else if (hasSizeOptions) {
        return `Selecione o tamanho do item <span class="item-number">${currentStep}</span>`;
      }

      return `Selecione as opções do item <span class="item-number">${currentStep}</span>`;
    }

    updateNavigationButtons(currentSlideIndex, totalSlides, hasCurrentSelection, allVariantsSelected, kitSelector) {
      const isLastSlide = currentSlideIndex === totalSlides - 1;
      const nextSlideIndex = currentSlideIndex + 1;
      const nextSlideHasOptions = isLastSlide || (kitSelector && kitSelector.hasAvailableOptions(nextSlideIndex));

      // Previous button
      if (this.elements.prevBtn) {
        this.elements.prevBtn.style.display = currentSlideIndex > 0 ? 'block' : 'none';
      }

      if (isLastSlide) {
        // Last slide - show add to cart
        if (this.elements.nextBtn) this.elements.nextBtn.style.display = 'none';
        if (this.elements.addToCartBtn) {
          this.elements.addToCartBtn.style.display = 'block';
          this.elements.addToCartBtn.disabled = !allVariantsSelected;
        }
      } else {
        // Not last slide - show next button with contextual states
        if (this.elements.nextBtn) {
          this.elements.nextBtn.style.display = 'block';

          // Determine button state and messaging
          if (!hasCurrentSelection) {
            this.elements.nextBtn.disabled = true;
            this.elements.nextBtn.textContent = "Selecione uma opção";
            this.elements.nextBtn.className = "kit-nav-btn kit-btn-blocked";
            this.elements.nextBtn.removeAttribute('title');
          } else if (!nextSlideHasOptions) {
            this.elements.nextBtn.disabled = true;
            this.elements.nextBtn.textContent = "Próximo item indisponível";
            this.elements.nextBtn.className = "kit-nav-btn kit-btn-unavailable";
            this.elements.nextBtn.title = "O próximo item não possui opções disponíveis";
          } else {
            this.elements.nextBtn.disabled = false;
            this.elements.nextBtn.textContent = "Próximo Item";
            this.elements.nextBtn.className = "kit-nav-btn";
            this.elements.nextBtn.removeAttribute('title');
          }
        }
        if (this.elements.addToCartBtn) {
          this.elements.addToCartBtn.style.display = 'none';
        }
      }
    }

    showNotification(message, type = 'info') {
      const notification = document.createElement('div');
      notification.className = `kit-notification kit-notification--${type}`;
      notification.textContent = message;

      document.body.appendChild(notification);

      setTimeout(() => notification.classList.add('kit-notification--visible'), 100);

      setTimeout(() => {
        notification.classList.remove('kit-notification--visible');
        setTimeout(() => notification.remove(), 300);
      }, 3000);
    }
  }

  // Navigation controller
  class NavigationController {
    constructor(swiper, state, slideManager) {
      this.swiper = swiper;
      this.state = state;
      this.slideManager = slideManager;
      this.currentSlideIndex = 0;
    }

    canNavigateForward() {
      return this.state.hasCompleteSelection(this.currentSlideIndex);
    }

    validateNavigation(targetIndex) {
      const isMovingForward = targetIndex > this.currentSlideIndex;
      const totalSlides = this.swiper.slides.length;
      const hasCurrentSelection = this.state.hasCompleteSelection(this.currentSlideIndex);

      console.log(`[NAV-DEBUG] 🔍 VALIDATING NAVIGATION:`, {
        from: this.currentSlideIndex,
        to: targetIndex,
        isMovingForward,
        totalSlides,
        hasCurrentSelection,
        needsValidation: isMovingForward && this.currentSlideIndex > 0 && this.currentSlideIndex < totalSlides - 1
      });

      if (isMovingForward && this.currentSlideIndex > 0 && this.currentSlideIndex < totalSlides - 1) {
        const canNavigate = this.state.hasCompleteSelection(this.currentSlideIndex);
        console.log(`[NAV-DEBUG] ${canNavigate ? '✅' : '❌'} Navigation ${canNavigate ? 'ALLOWED' : 'BLOCKED'}:`, {
          reason: canNavigate ? 'Has complete selection' : 'Missing selection',
          currentSelection: this.state.getSelection(this.currentSlideIndex)
        });
        return canNavigate;
      }

      console.log(`[NAV-DEBUG] ✅ Navigation ALLOWED (no validation needed)`);
      return true;
    }

    goToSlide(index, force = false) {
      if (!force && !this.validateNavigation(index)) {
        return false;
      }

      this.swiper.slideTo(index);
      return true;
    }

    onSlideChange(activeIndex) {
      console.log(`[NAV-DEBUG] === SLIDE CHANGE EVENT ===`, {
        from: this.currentSlideIndex,
        to: activeIndex,
        timestamp: Date.now()
      });

      const previousIndex = this.currentSlideIndex;
      this.currentSlideIndex = activeIndex;
      const slide = this.swiper.slides[activeIndex];

      if (slide) {
        console.log(`[NAV-DEBUG] 🎯 Initializing slide ${activeIndex} after navigation from ${previousIndex}`);
        this.slideManager.initializeSlide(slide, activeIndex);
      } else {
        console.error(`[NAV-DEBUG] ❌ No slide found at index ${activeIndex}`);
      }
    }
  }

  // Slide management
  class SlideManager {
    constructor(state, uiManager, productManager) {
      this.state = state;
      this.uiManager = uiManager;
      this.productManager = productManager;
    }

    initializeSlide(slide, slideIndex) {
      const productId = slide.dataset.productId;

      console.log(`[SLIDE-DEBUG] === INITIALIZING SLIDE ${slideIndex} ===`, {
        slideIndex,
        productId,
        timestamp: Date.now()
      });

      // Register product-slide relationship
      this.state.registerProductSlide(productId, slideIndex);

      // Check for existing slide selection first (preserves user's changes)
      const existingSelection = this.state.getSelection(slideIndex);
      if (existingSelection) {
        console.log(`[SLIDE-DEBUG] ⚡ EXISTING SELECTION FOUND for slide ${slideIndex}:`, {
          selection: existingSelection,
          action: 'RESTORING_SELECTION'
        });
        this.restoreSelection(slide, slideIndex, existingSelection);
        return;
      }

      // Check for product memory from previous slides (for repeated products)
      const productMemory = this.state.getProductMemory(productId);
      const hasBeenSelected = this.state.hasProductBeenSelected(productId, slideIndex);

      console.log(`[SLIDE-DEBUG] 🧠 PRODUCT MEMORY CHECK for slide ${slideIndex}:`, {
        productId,
        hasProductMemory: !!productMemory,
        productMemory: productMemory ? {
          color: productMemory.color,
          size: productMemory.size,
          variantId: productMemory.variantId
        } : null,
        hasBeenSelected,
        decision: productMemory && hasBeenSelected ? 'APPLYING_MEMORY' : 'NEW_SLIDE'
      });

      if (productMemory && hasBeenSelected) {
        console.log(`[SLIDE-DEBUG] 🎯 APPLYING PRODUCT MEMORY for slide ${slideIndex}`);
        // Try to apply remembered selection
        this.applyProductMemory(slide, slideIndex, productId, productMemory);
      } else {
        console.log(`[SLIDE-DEBUG] 🆕 INITIALIZING NEW SLIDE ${slideIndex}`);
        // Initialize as new slide
        this.initializeNewSlide(slide, slideIndex);
      }
    }

    applyProductMemory(slide, slideIndex, productId, memory) {
      console.log(`[MEMORY-DEBUG] 💾 APPLYING PRODUCT MEMORY to slide ${slideIndex}:`, {
        productId,
        rememberedColor: memory.color,
        rememberedSize: memory.size,
        rememberedVariantId: memory.variantId
      });

      // Handle single variant products
      const singleVariant = slide.querySelector('.single-variant-selector');
      if (singleVariant) {
        console.log(`[MEMORY-DEBUG] Single variant product - skipping memory application`);
        this.handleSingleVariant(slideIndex, productId, singleVariant);
        return;
      }

      // Update input IDs first
      this.updateInputIds(slide, slideIndex, productId);

      // Try to apply the remembered color and size
      const colorInput = this.findColorInput(slide, memory.color);
      if (colorInput && !colorInput.disabled) {
        console.log(`[MEMORY-DEBUG] ✅ Found matching color input for "${memory.color}", applying...`);
        colorInput.checked = true;
        this.uiManager.updateColorSelection(slide, colorInput);

        // Set initial selection with color and remembered variantId
        // This ensures the button is enabled immediately when memory is applied
        // The variantId will be validated/updated when sizes are loaded
        this.state.setSelection(slideIndex, {
          productId,
          color: memory.color,
          size: memory.size,
          variantId: memory.variantId // Use remembered variantId temporarily
        });

        console.log(`[MEMORY-DEBUG] 📏 Loading sizes for color "${memory.color}" with preserved size "${memory.size}"`);
        // Load sizes and try to preserve the remembered size
        // This will validate and update the variantId if needed
        this.productManager.loadSizesForColor(slide, colorInput, slideIndex, memory.size);
      } else {
        console.log(`[MEMORY-DEBUG] ⚠️ Could not apply memory - color "${memory.color}" not available, falling back to auto-select`);
        // Fallback if remembered color not available
        this.autoSelectFirstColor(slide, slideIndex, productId);
      }
    }

    initializeNewSlide(slide, slideIndex) {
      const productId = slide.dataset.productId;

      // Handle single variant products
      const singleVariant = slide.querySelector('.single-variant-selector');
      if (singleVariant) {
        this.handleSingleVariant(slideIndex, productId, singleVariant);
        return;
      }

      // Handle multi-variant products
      this.updateInputIds(slide, slideIndex, productId);
      this.autoSelectFirstColor(slide, slideIndex, productId);
    }

    handleSingleVariant(slideIndex, productId, singleVariant) {
      const variantId = singleVariant.dataset.variantId;
      const available = singleVariant.dataset.available === 'true';

      if (available) {
        this.state.setSelection(slideIndex, {
          productId,
          color: null,
          size: null,
          variantId
        });
      }
    }

    updateInputIds(slide, slideIndex, productId) {
      const allColorInputs = slide.querySelectorAll('input[type="radio"][data-color-name]');

      allColorInputs.forEach((input, index) => {
        const originalId = input.id;
        const newId = Utils.generateUniqueId('kit-color', slideIndex, productId, String(index + 1));
        const newName = `kit-${slideIndex}-${productId}-option1`;

        input.id = newId;
        input.name = newName;

        const label = slide.querySelector(`label[for="${originalId}"]`);
        if (label) {
          label.setAttribute('for', newId);
        }
      });
    }

    autoSelectFirstColor(slide, slideIndex, productId) {
      const allColorInputs = slide.querySelectorAll('input[type="radio"][data-color-name]');

      let selectedColorInput = null;
      for (const input of allColorInputs) {
        if (!input.disabled) {
          input.checked = true;
          selectedColorInput = input;
          break;
        }
      }

      if (selectedColorInput) {
        this.uiManager.updateColorSelection(slide, selectedColorInput);

        const colorName = selectedColorInput.dataset.colorName || selectedColorInput.value;
        this.state.setSelection(slideIndex, {
          productId,
          color: colorName,
          size: null,
          variantId: null
        });

        this.productManager.loadSizesForColor(slide, selectedColorInput, slideIndex, null);
      }
    }

    restoreSelection(slide, slideIndex, selection) {
      const productId = slide.dataset.productId;
      this.updateInputIds(slide, slideIndex, productId);

      if (selection.color) {
        const colorInput = this.findColorInput(slide, selection.color);
        if (colorInput) {
          colorInput.checked = true;
          this.uiManager.updateColorSelection(slide, colorInput);
          this.productManager.loadSizesForColor(slide, colorInput, slideIndex, selection.size);
        } else {
          // Color no longer available, reinitialize
          this.initializeNewSlide(slide, slideIndex);
        }
      }
    }

    findColorInput(slide, colorName) {
      const allColorInputs = slide.querySelectorAll('input[type="radio"][data-color-name]');
      return Array.from(allColorInputs).find(input =>
        (input.dataset.colorName || input.value) === colorName && !input.disabled
      );
    }
  }

  // Product data management
  class ProductManager {
    constructor(state, uiManager) {
      this.state = state;
      this.uiManager = uiManager;
    }

    loadSizesForColor(slide, colorInput, slideIndex, preserveSize) {
      const colorData = this.getColorData(slide, colorInput);
      const productData = this.getProductData(slide);

      console.log(`[STOCK-DEBUG] === Loading sizes for color "${colorInput.dataset.colorName || colorInput.value}" on slide ${slideIndex} ===`);

      if (!colorData || !productData) return;

      const sizeOptions = this.generateSizeOptions(productData, colorData, colorInput);
      const { sizesHTML, sizeToSelect } = this.buildSizesHTML(sizeOptions, slideIndex, slide.dataset.productId, preserveSize);

      this.updateSizesContainer(slide, sizesHTML, slideIndex);

      if (preserveSize || sizeToSelect) {
        this.handlePreservedSize(slide, slideIndex, preserveSize || sizeToSelect.size, sizeOptions);
      }
    }

    getColorData(slide, colorInput) {
      const colorLabel = slide.querySelector(`label[for="${colorInput.id}"]`);
      let colorDataScript = colorLabel?.nextElementSibling;

      if (!colorDataScript?.classList.contains('kit-color-data')) {
        const allColorData = slide.querySelectorAll('.kit-color-data');
        for (const script of allColorData) {
          const data = JSON.parse(script.textContent);
          if (data.value === colorInput.value) {
            colorDataScript = script;
            break;
          }
        }
      }

      return colorDataScript ? JSON.parse(colorDataScript.textContent) : null;
    }

    getProductData(slide) {
      const productDataScript = slide.querySelector('.kit-product-data');
      return productDataScript ? JSON.parse(productDataScript.textContent) : null;
    }

    // Public method to get product data from slide (used by KitSelector)
    getProductDataFromSlide(slide) {
      return this.getProductData(slide);
    }

    generateSizeOptions(productData, colorData, colorInput) {
      const colorOptionIndex = this.findOptionIndex(productData.options, ['Cor', 'Color']);
      const sizeOptionIndex = this.findOptionIndex(productData.options, ['Tamanho', 'Size']);

      const selectedColor = colorInput.dataset.colorName || colorInput.value || colorData.value;

      const colorVariants = productData.variants.filter(variant =>
        variant.options[colorOptionIndex] === selectedColor
      );

      const sizeOptions = new Map();
      colorVariants.forEach(variant => {
        const size = variant.options[sizeOptionIndex];
        if (size && !sizeOptions.has(size)) {
          sizeOptions.set(size, {
            available: variant.available,
            variantId: variant.id,
            price: variant.price,
            compareAtPrice: variant.compare_at_price
          });

          console.log(`[STOCK-DEBUG] Found size option: ${size}`, {
            variantId: variant.id,
            available: variant.available,
            inventory: variant.inventory_quantity || 'N/A',
            inventoryPolicy: variant.inventory_policy || 'N/A'
          });
        }
      });

      return Utils.sortSizes(Array.from(sizeOptions.entries()));
    }

    findOptionIndex(options, possibleNames) {
      for (const name of possibleNames) {
        const index = options.indexOf(name);
        if (index !== -1) return index;
      }
      return -1;
    }

    buildSizesHTML(sortedSizes, slideIndex, productId, preserveSize) {
      let sizeToSelect = null;
      let fallbackSize = null;

      let sizesHTML = sortedSizes.map(([size, data]) => {
        // Check if this variant has already been selected globally
        // Now using global counter for accurate tracking
        const usageCount = this.state.getVariantUsageCount(data.variantId);
        const availableStock = this.state.getAvailableStock(data.variantId);
        const remainingStock = Math.max(0, availableStock - usageCount);
        const isReserved = remainingStock === 0;

        console.log(`[STOCK-DEBUG] 📊 Size ${size} (Variant ${data.variantId}):`, {
          availableInShopify: data.available,
          totalStock: availableStock,
          usedInKit: usageCount,
          remainingStock: remainingStock,
          isReserved: isReserved,
          canSelect: data.available && !isReserved
        });

        // Disable if not available OR if already reserved
        const disabled = (!data.available || isReserved) ? 'disabled' : '';
        const isDisabledClass = (!data.available || isReserved) ? 'is-disabled' : '';
        const sizeInputId = Utils.generateUniqueId('kit-size-dynamic', slideIndex, productId, size);

        let checked = '';
        let isSelectedClass = '';

        // Try to preserve the exact size if available and not reserved
        if (preserveSize && size === preserveSize && data.available && !isReserved) {
          checked = 'checked';
          isSelectedClass = 'is-selected';
          sizeToSelect = { size, variantId: data.variantId };
        }

        // Set first available size as fallback
        if (!fallbackSize && data.available && !isReserved && !sizeToSelect) {
          fallbackSize = { size, variantId: data.variantId };
        }

        const htmlOutput = `
          <input
            type="radio"
            class="sr-only"
            id="${sizeInputId}"
            name="kit-${slideIndex}-${productId}-option2"
            value="${size}"
            data-option-position="2"
            data-size-name="${size}"
            data-variant-id="${data.variantId}"
            ${disabled}
            ${checked}
          >
          <label
            class="block-swatch ${isDisabledClass} ${isSelectedClass}"
            for="${sizeInputId}"
          >
            ${size}
          </label>
        `;

        console.log(`[HTML-DEBUG] 📝 Generated HTML for size ${size} on slide ${slideIndex}:`, {
          size: size,
          variantId: data.variantId,
          slideIndex: slideIndex,
          isReserved: isReserved,
          remainingStock: remainingStock,
          disabledAttribute: disabled,
          isDisabledClass: isDisabledClass,
          willBeDisabled: disabled === 'disabled',
          htmlSnippet: htmlOutput.substring(0, 200)
        });

        return htmlOutput;
      }).join('');

      // Use fallback if preserved size not available
      if (!sizeToSelect && fallbackSize && preserveSize) {
        // Apply fallback selection to first available size
        const fallbackInputId = Utils.generateUniqueId('kit-size-dynamic', slideIndex, productId, fallbackSize.size);
        sizesHTML = sizesHTML.replace(
          new RegExp(`id="${fallbackInputId}"([^>]*)>`),
          `id="${fallbackInputId}"$1 checked>`
        );
        sizesHTML = sizesHTML.replace(
          new RegExp(`for="${fallbackInputId}"([^>]*block-swatch[^>]*)>`),
          `for="${fallbackInputId}"$1 is-selected>`
        );
        sizeToSelect = fallbackSize;
      }

      return { sizesHTML, sizeToSelect };
    }

    updateSizesContainer(slide, sizesHTML, slideIndex) {
      const sizesContainer = slide.querySelector('fieldset[data-option-type="size"] .variant-picker__option-values');
      if (sizesContainer) {
        console.log(`[DOM-DEBUG] 🔄 Updating sizes container for slide ${slideIndex}`);

        sizesContainer.innerHTML = sizesHTML;

        // Verify what actually got inserted into DOM
        const insertedInputs = sizesContainer.querySelectorAll('input[type="radio"]');
        console.log(`[DOM-DEBUG] ✅ Inserted ${insertedInputs.length} size inputs into DOM`);

        insertedInputs.forEach((input, index) => {
          console.log(`[DOM-DEBUG] 📋 Input ${index} in DOM:`, {
            id: input.id,
            variantId: input.dataset.variantId,
            size: input.dataset.sizeName,
            disabled: input.disabled,
            hasDisabledAttr: input.hasAttribute('disabled'),
            checked: input.checked,
            outerHTML: input.outerHTML.substring(0, 150)
          });
        });

        this.attachDirectListeners(sizesContainer, slideIndex, slide);
      }
    }

    attachDirectListeners(sizesContainer, slideIndex, slide) {
      // For intermediate slides, attach direct listeners to ensure events are captured
      const isIntermediateSlide = slideIndex > 0;

      if (isIntermediateSlide) {
        const newSizeInputs = sizesContainer.querySelectorAll('input[type="radio"]');
        const sizeLabels = sizesContainer.querySelectorAll('label.block-swatch');

        console.log(`[LISTENER-DEBUG] 🎧 Attaching listeners for slide ${slideIndex}:`, {
          totalInputs: newSizeInputs.length,
          totalLabels: sizeLabels.length,
          slideIndex: slideIndex
        });

        newSizeInputs.forEach(input => {
          input.addEventListener('click', (e) => {
            console.log(`[LISTENER-DEBUG] 🖱️ INPUT CLICK on slide ${slideIndex}:`, {
              variantId: input.dataset.variantId,
              size: input.dataset.sizeName,
              disabled: input.disabled,
              hasDisabledAttr: input.hasAttribute('disabled'),
              checked: input.checked
            });

            if (input.disabled) {
              console.warn(`[LISTENER-DEBUG] ⛔ BLOCKED: Input is disabled!`);
              e.preventDefault();
              e.stopPropagation();
              return;
            }

            e.target.checked = true;
            this.handleSizeSelection(e.target);
          });

          input.addEventListener('change', (e) => {
            console.log(`[LISTENER-DEBUG] 🔄 INPUT CHANGE on slide ${slideIndex}:`, {
              variantId: input.dataset.variantId,
              size: input.dataset.sizeName,
              disabled: input.disabled,
              checked: input.checked
            });

            if (input.disabled) {
              console.warn(`[LISTENER-DEBUG] ⛔ BLOCKED: Input is disabled (change event)!`);
              e.preventDefault();
              e.stopPropagation();
              return;
            }

            this.handleSizeSelection(e.target);
          });
        });

        sizeLabels.forEach(label => {
          label.addEventListener('click', (e) => {
            const inputId = label.getAttribute('for');
            const input = document.getElementById(inputId);

            console.log(`[LISTENER-DEBUG] 🏷️ LABEL CLICK on slide ${slideIndex}:`, {
              inputId: inputId,
              inputFound: !!input,
              inputDisabled: input?.disabled,
              hasDisabledAttr: input?.hasAttribute('disabled'),
              labelClasses: label.className,
              hasIsDisabledClass: label.classList.contains('is-disabled')
            });

            e.preventDefault();
            e.stopPropagation();

            if (input && !input.disabled) {
              console.log(`[LISTENER-DEBUG] ✅ Label click ALLOWED - triggering selection`);
              input.checked = true;
              this.handleSizeSelection(input);
            } else {
              console.warn(`[LISTENER-DEBUG] ⛔ BLOCKED: Input is ${input ? 'disabled' : 'not found'}!`);
            }
          });
        });
      }
    }

    handlePreservedSize(slide, slideIndex, preserveSize, sizeOptions) {
      const currentSelection = this.state.getSelection(slideIndex);
      if (!currentSelection) return;

      const productId = slide.dataset.productId;
      const sizeOption = sizeOptions.find(([size]) => size === preserveSize);

      // Check if the preserved size is available and not reserved
      if (sizeOption && sizeOption[1].available) {
        // Check if this variant is reserved globally
        const usageCount = this.state.getVariantUsageCount(sizeOption[1].variantId);
        const availableStock = this.state.getAvailableStock(sizeOption[1].variantId);
        const isReserved = usageCount >= availableStock;

        console.log(`[STOCK-DEBUG] 🔍 Checking preserved size "${preserveSize}" (Variant ${sizeOption[1].variantId}):`, {
          stockQuantity: availableStock,
          usedInKit: usageCount,
          isReserved: isReserved,
          decision: !isReserved ? 'USE_PRESERVED' : 'FIND_ALTERNATIVE'
        });
        
        if (!isReserved) {
          // Size is available and not reserved - use it
          currentSelection.size = preserveSize;
          currentSelection.variantId = String(sizeOption[1].variantId);
          this.state.setSelection(slideIndex, currentSelection);
          
          const sizeLabel = slide.querySelector('fieldset[data-option-type="size"] .selected-size');
          if (sizeLabel) {
            sizeLabel.textContent = preserveSize;
          }
          return;
        }
      }
      
      // If preserved size is not available or is reserved, find first available alternative
      console.log(`[STOCK-DEBUG] 🔄 Searching for alternative sizes...`);
      for (const [size, data] of sizeOptions) {
        if (data.available) {
          const usageCount = this.state.getVariantUsageCount(data.variantId);
          const availableStock = this.state.getAvailableStock(data.variantId);
          const isReserved = usageCount >= availableStock;

          console.log(`[STOCK-DEBUG] 🔄 Checking alternative size "${size}" (Variant ${data.variantId}):`, {
            stockQuantity: availableStock,
            usedInKit: usageCount,
            isReserved: isReserved
          });
          
          if (!isReserved) {
            // Found an available alternative
            currentSelection.size = size;
            currentSelection.variantId = String(data.variantId);
            this.state.setSelection(slideIndex, currentSelection);
            
            const sizeLabel = slide.querySelector('fieldset[data-option-type="size"] .selected-size');
            if (sizeLabel) {
              sizeLabel.textContent = size;
            }
            
            console.log(`[STOCK-DEBUG] ✅ Alternative found: Using size "${size}" instead of "${preserveSize}"`);
            console.log(`[MEMORY-DEBUG] Preserved size "${preserveSize}" not available, using "${size}" instead`);
            break;
          }
        }
      }
    }

    handleSizeSelection(input) {
      console.log(`[SELECTION-DEBUG] 🎯 handleSizeSelection called:`, {
        disabled: input.disabled,
        hasDisabledAttr: input.hasAttribute('disabled'),
        variantId: input.dataset.variantId,
        size: input.dataset.sizeName
      });

      if (input.disabled) {
        console.warn(`[SELECTION-DEBUG] ⛔ BLOCKED in handleSizeSelection: Input is disabled!`);
        return;
      }

      const slide = input.closest('.swiper-slide');
      const slideIndex = parseInt(slide.dataset.swiperSlideIndex) ||
                         Array.from(slide.parentElement.children).indexOf(slide);
      const productId = slide.dataset.productId;
      const sizeName = input.dataset.sizeName || input.value;
      const variantId = input.dataset.variantId;

      // Double-check stock before allowing selection
      const usageCount = this.state.getVariantUsageCount(variantId);
      const availableStock = this.state.getAvailableStock(variantId);
      const remainingStock = Math.max(0, availableStock - usageCount);

      console.log(`[SELECTION-DEBUG] 📊 Stock check before selection:`, {
        variantId: variantId,
        size: sizeName,
        slideIndex: slideIndex,
        totalStock: availableStock,
        currentUsage: usageCount,
        remainingStock: remainingStock,
        canSelect: remainingStock > 0
      });

      if (remainingStock <= 0) {
        console.error(`[SELECTION-DEBUG] ❌ BLOCKED: No stock available for variant ${variantId}!`);
        return;
      }

      console.log(`[SELECTION-DEBUG] ✅ Selection ALLOWED - proceeding...`);

      const currentSelection = this.state.getSelection(slideIndex) || { productId };
      currentSelection.size = sizeName;
      // Ensure variantId is always a string for consistency
      currentSelection.variantId = String(variantId);
      this.state.setSelection(slideIndex, currentSelection);

      this.uiManager.updateSizeSelection(slide, input);
    }
  }

  // Cart management
  class CartManager {
    constructor(state, uiManager, kitId) {
      this.state = state;
      this.uiManager = uiManager;
      this.kitId = kitId;
    }

    async addKitToCart() {
      const selections = this.state.getAllSelections();
      const addToCartBtn = this.uiManager.elements.addToCartBtn;

      if (addToCartBtn) {
        addToCartBtn.disabled = true;
      }

      try {
        this.dispatchLoadingStart();
        const sectionsToBundle = this.prepareBundleSections();
        const formData = this.buildFormData(selections, sectionsToBundle);

        const response = await this.submitToCart(formData);
        const responseJson = await response.json();

        await this.handleCartSuccess(responseJson);
      } catch (error) {
        this.handleCartError(error);
      } finally {
        if (addToCartBtn) {
          addToCartBtn.disabled = false;
        }
      }
    }

    dispatchLoadingStart() {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    }

    prepareBundleSections() {
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));
      return sectionsToBundle;
    }

    buildFormData(selections, sectionsToBundle) {
      const formData = new FormData();

      console.log('[KIT-DEBUG] === INICIANDO AGRUPAMENTO DE VARIANTES ===');
      console.log('[KIT-DEBUG] Total de seleções:', selections.length);

      // Group identical variants together for better UX
      const groupedVariants = new Map();

      selections.forEach(([slideIndex, selection]) => {
        if (selection?.variantId) {
          // IMPORTANT: Normalize variantId to string to ensure consistent comparison
          const normalizedVariantId = String(selection.variantId);

          console.log(`[KIT-DEBUG] Slide ${slideIndex}:`, {
            originalVariantId: selection.variantId,
            originalType: typeof selection.variantId,
            normalizedVariantId: normalizedVariantId,
            productId: selection.productId,
            color: selection.color,
            size: selection.size
          });

          if (groupedVariants.has(normalizedVariantId)) {
            console.log(`[KIT-DEBUG] ✅ Variant ${normalizedVariantId} JÁ EXISTE, incrementando...`);
            // Variant already exists, increment quantity and track slide index
            const existing = groupedVariants.get(normalizedVariantId);
            existing.quantity += 1;
            existing.slideIndexes.push(slideIndex + 1); // +1 for human-readable index
          } else {
            console.log(`[KIT-DEBUG] ➕ Variant ${normalizedVariantId} NOVA, adicionando ao Map...`);
            // New variant
            groupedVariants.set(normalizedVariantId, {
              variantId: normalizedVariantId,
              quantity: 1,
              slideIndexes: [slideIndex + 1], // +1 for human-readable index
              productId: selection.productId,
              color: selection.color,
              size: selection.size
            });
          }
        }
      });

      console.log('[KIT-DEBUG] === RESULTADO DO AGRUPAMENTO ===');
      console.log('[KIT-DEBUG] Total de grupos criados:', groupedVariants.size);
      groupedVariants.forEach((group, variantId) => {
        console.log(`[KIT-DEBUG] Variant ${variantId} (tipo: ${typeof variantId}):`, {
          quantity: group.quantity,
          slides: group.slideIndexes.join(', '),
          color: group.color,
          size: group.size
        });
      });

      // Build form data with grouped variants
      let itemIndex = 0;
      groupedVariants.forEach((group) => {
        console.log(`[KIT-DEBUG] Adicionando ao FormData - Item ${itemIndex}:`, {
          id: group.variantId,
          quantity: group.quantity,
          properties: {
            _kit_id: this.kitId,
            _kit_items: group.slideIndexes.join(', '),
            _kit_total_items: selections.length
          }
        });

        formData.append(`items[${itemIndex}][id]`, group.variantId);
        formData.append(`items[${itemIndex}][quantity]`, group.quantity);

        // Kit tracking properties
        formData.append(`items[${itemIndex}][properties][_kit_id]`, this.kitId);
        formData.append(`items[${itemIndex}][properties][_kit_items]`, group.slideIndexes.join(', '));
        formData.append(`items[${itemIndex}][properties][_kit_total_items]`, selections.length);

        // Add variant info for clarity (optional but helpful)
        if (group.quantity > 1) {
          formData.append(`items[${itemIndex}][properties][_kit_grouped]`, `${group.quantity}x ${group.color || ''} ${group.size || ''}`);
        }

        itemIndex++;
      });

      console.log('[KIT-DEBUG] === FORMDATA FINAL ===');
      console.log('[KIT-DEBUG] Total de itens no FormData:', itemIndex);

      // Log FormData content for debugging
      for (let pair of formData.entries()) {
        if (pair[0].startsWith('items')) {
          console.log(`[KIT-DEBUG] ${pair[0]}: ${pair[1]}`);
        }
      }

      formData.set("sections", sectionsToBundle.join(","));
      formData.set("sections_url", window.location.pathname);

      return formData;
    }

    async submitToCart(formData) {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });

      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error('Failed to add items to cart: ' + errorText);
      }

      return response;
    }

    async handleCartSuccess(responseJson) {
      console.log('[KIT-DEBUG] === RESPOSTA DO SHOPIFY ===');
      console.log('[KIT-DEBUG] Items adicionados:', responseJson);

      const cartContent = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();

      console.log('[KIT-DEBUG] Conteúdo do carrinho após adicionar:', cartContent.items?.length, 'itens');

      if (responseJson.sections) {
        cartContent.sections = responseJson.sections;
      }

      this.uiManager.wrapper.dispatchEvent(new CustomEvent("variant:add", {
        bubbles: true,
        detail: {
          items: responseJson.hasOwnProperty("items") ? responseJson.items : [responseJson],
          cart: cartContent,
          onSuccessDo: "force_open_drawer"
        }
      }));

      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "variant:add",
          cart: cartContent,
          onSuccessDo: "force_open_drawer"
        }
      }));
    }

    handleCartError(error) {
      this.uiManager.showNotification('Erro ao adicionar kit ao carrinho. Por favor, tente novamente.', 'error');
    }
  }

  // Main KitSelector class
  class KitSelector {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.swiper = null;
      this.kitId = wrapper.dataset.kitId;

      this.state = new KitState();
      this.uiManager = new UIManager(wrapper, this.state);
      this.productManager = new ProductManager(this.state, this.uiManager);
      this.cartManager = new CartManager(this.state, this.uiManager, this.kitId);

      this.init();
    }

    init() {
      this.initSwiper();
      setTimeout(() => {
        this.slideManager = new SlideManager(this.state, this.uiManager, this.productManager);
        this.navigationController = new NavigationController(this.swiper, this.state, this.slideManager);

        // Bind product manager's handleSizeSelection to this context
        this.productManager.handleSizeSelection = this.productManager.handleSizeSelection.bind(this.productManager);

        this.bindEvents();
        this.setupStateObserver();
      }, 100);
    }

    initSwiper() {
      const swiperElement = this.wrapper.querySelector('.kits-sliders');

      this.swiper = new Swiper(swiperElement, {
        draggable: false,
        allowTouchMove: false,
        noSwipingClass: 'kits-sliders',
        // Navigation removed - handled manually to prevent double navigation
        pagination: {
          el: '.swiper-pagination',
          type: 'fraction',
        },
        on: {
          init: (swiper) => this.onSlideInit(swiper),
          slideChange: (swiper) => this.onSlideChange(swiper)
        }
      });
    }

    bindEvents() {
      // Delegated event handling
      this.wrapper.addEventListener('change', (e) => {
        if (e.target.type === 'radio') {
          this.handleRadioChange(e.target);
        }
      });

      this.wrapper.addEventListener('click', (e) => {
        this.handleLabelClick(e);
        this.handleButtonClick(e);
      });
    }

    handleRadioChange(input) {
      const optionPosition = input.dataset.optionPosition;

      if (input.dataset.colorName || optionPosition === '1') {
        this.handleColorChange(input);
      } else if (input.dataset.sizeName || optionPosition === '2') {
        this.productManager.handleSizeSelection(input);
      }
    }

    handleColorChange(input) {
      const slide = input.closest('.swiper-slide');
      const slideIndex = this.navigationController?.currentSlideIndex || 0;
      const productId = slide.dataset.productId;
      const colorName = input.dataset.colorName || input.value;
      const imageUrl = input.dataset.imageUrl;

      const currentSelection = this.state.getSelection(slideIndex) || { productId };
      const previousSize = currentSelection.size;

      currentSelection.color = colorName;
      currentSelection.size = null;
      currentSelection.variantId = null;
      this.state.setSelection(slideIndex, currentSelection);

      if (!input.checked) input.checked = true;

      this.uiManager.updateColorSelection(slide, input);
      this.uiManager.updateProductImage(slide, imageUrl);
      this.productManager.loadSizesForColor(slide, input, slideIndex, previousSize);
      this.validateCascade(slideIndex);
    }

    handleLabelClick(e) {
      const label = e.target.closest('label.block-swatch');
      if (!label) return;

      const forId = label.getAttribute('for');
      const slide = label.closest('.swiper-slide');
      const input = slide?.querySelector(`#${forId}`) || document.getElementById(forId);

      if (input?.type === 'radio' && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    handleButtonClick(e) {
      if (e.target.id === 'add-all-kit-to-cart') {
        this.cartManager.addKitToCart();
      } else if (e.target.id === 'kit-next-item' && !e.target.disabled) {
        // Use smart navigation that skips unavailable slides
        const nextSlideIndex = this.navigationController.currentSlideIndex + 1;
        if (nextSlideIndex < this.swiper.slides.length) {
          // Check if next slide has options, if not use smart navigation
          if (this.hasAvailableOptions(nextSlideIndex)) {
            this.swiper.slideNext();
          } else {
            this.navigateToNextAvailableSlide();
          }
        }
      } else if (e.target.id === 'kit-prev-item') {
        this.swiper.slidePrev();
      }
    }

    setupStateObserver() {
      this.state.addObserver(() => {
        this.updateUI();
      });
    }

    onSlideInit(swiper) {
      console.log(`[SWIPER-DEBUG] 🎬 SWIPER INITIALIZED:`, {
        totalSlides: swiper.slides.length,
        activeIndex: swiper.activeIndex,
        timestamp: Date.now()
      });

      const slide = swiper.slides[0];
      if (!this.slideManager) {
        this.slideManager = new SlideManager(this.state, this.uiManager, this.productManager);
      }
      this.slideManager.initializeSlide(slide, 0);
      this.updateUI();
    }

    onSlideChange(swiper) {
      console.log(`[SWIPER-DEBUG] 🔄 SWIPER SLIDE CHANGE:`, {
        activeIndex: swiper.activeIndex,
        previousIndex: swiper.previousIndex,
        realIndex: swiper.realIndex,
        isBeginning: swiper.isBeginning,
        isEnd: swiper.isEnd,
        timestamp: Date.now()
      });

      if (!this.navigationController) {
        console.error(`[SWIPER-DEBUG] ❌ Navigation controller not ready`);
        return;
      }

      const validationResult = this.navigationController.validateNavigation(swiper.activeIndex);
      console.log(`[SWIPER-DEBUG] 🔍 Navigation validation result:`, {
        targetIndex: swiper.activeIndex,
        currentIndex: this.navigationController.currentSlideIndex,
        isValid: validationResult
      });

      if (!validationResult) {
        console.log(`[SWIPER-DEBUG] 🚫 NAVIGATION BLOCKED - Rolling back to slide ${this.navigationController.currentSlideIndex}`);
        setTimeout(() => swiper.slideTo(this.navigationController.currentSlideIndex, 0), 100);
        return;
      }

      console.log(`[SWIPER-DEBUG] ✅ Navigation proceeding to slide ${swiper.activeIndex}`);
      this.navigationController.onSlideChange(swiper.activeIndex);
      this.updateUI();
    }

    updateUI() {
      if (!this.swiper?.slides || !this.navigationController) return;

      const currentIndex = this.navigationController.currentSlideIndex;
      const totalSlides = this.swiper.slides.length;
      const currentSlide = this.swiper.slides[currentIndex];
      const hasCurrentSelection = this.state.hasCompleteSelection(currentIndex);
      const allVariantsSelected = this.areAllVariantsSelected();

      this.uiManager.updateStepIndicator(currentIndex, totalSlides, currentSlide);
      this.uiManager.updateNavigationButtons(currentIndex, totalSlides, hasCurrentSelection, allVariantsSelected, this);
    }

    validateCascade(fromSlideIndex) {
      const totalSlides = this.swiper.slides.length;

      for (let i = fromSlideIndex + 1; i < totalSlides; i++) {
        const selection = this.state.getSelection(i);

        if (selection?.variantId) {
          const slide = this.swiper.slides[i];
          if (slide && !this.isSelectionValid(slide, selection)) {
            selection.variantId = null;
            selection.size = null;
            this.state.setSelection(i, selection);
          }
        }
      }
    }

    isSelectionValid(slide, selection) {
      if (!selection?.variantId) return true;

      const productDataScript = slide.querySelector('.kit-product-data');
      if (!productDataScript) return false;

      try {
        const productData = JSON.parse(productDataScript.textContent);
        const variant = productData.variants.find(v => v.id === selection.variantId);
        return variant?.available || false;
      } catch (e) {
        return false;
      }
    }

    areAllVariantsSelected() {
      const totalSlides = this.swiper.slides.length;

      for (let i = 0; i < totalSlides; i++) {
        if (!this.state.hasCompleteSelection(i)) {
          return false;
        }
      }

      return true;
    }

    // Check if a slide has any available options for selection
    hasAvailableOptions(slideIndex) {
      const slide = this.swiper.slides[slideIndex];
      if (!slide) return false;

      // Check single variant products
      const singleVariant = slide.querySelector('.single-variant-selector');
      if (singleVariant) {
        return singleVariant.dataset.available === 'true';
      }

      // Check multi-variant products - any size available for any color
      const productId = slide.dataset.productId;
      const productData = this.productManager.getProductDataFromSlide(slide);
      if (!productData) return false;

      // Check if any variant is available and not reserved
      return productData.variants.some(variant => {
        if (!variant.available) return false;
        const usageCount = this.state.getVariantUsageCount(variant.id);
        const availableStock = this.state.getAvailableStock(variant.id);
        return usageCount < availableStock;
      });
    }

    // Analyze if kit can be completed with current stock
    analyzeKitFeasibility() {
      const totalSlides = this.swiper.slides.length;
      const feasibilityReport = {
        canComplete: true,
        blockedSlides: [],
        recommendedActions: [],
        currentSlide: this.navigationController?.currentSlideIndex || 0
      };

      for (let i = 0; i < totalSlides; i++) {
        const slideHasOptions = this.hasAvailableOptions(i);
        const currentSelection = this.state.getSelection(i);

        if (!slideHasOptions && !this.state.hasCompleteSelection(i)) {
          feasibilityReport.canComplete = false;
          feasibilityReport.blockedSlides.push({
            slideIndex: i,
            reason: 'NO_OPTIONS_AVAILABLE',
            productId: this.swiper.slides[i]?.dataset.productId
          });
        }
      }

      return feasibilityReport;
    }

    // Show dialog when kit cannot be completed
    showKitBlockedDialog(feasibilityReport) {
      const existingDialog = document.querySelector('.kit-completion-dialog');
      if (existingDialog) existingDialog.remove();

      const dialog = document.createElement('div');
      dialog.className = 'kit-completion-dialog';

      const blockedItems = feasibilityReport.blockedSlides.map(block =>
        `Item ${block.slideIndex + 1}`
      ).join(', ');

      dialog.innerHTML = `
        <div class="kit-dialog-overlay">
          <div class="kit-dialog-content">
            <button class="kit-dialog-close" aria-label="Fechar">&times;</button>
            <h3>⚠️ Kit Não Pode Ser Completado</h3>
            <p>Os seguintes itens estão sem estoque disponível: <strong>${blockedItems}</strong></p>
            <p class="kit-dialog-subtitle">Escolha uma das opções abaixo para continuar:</p>
            <div class="kit-dialog-actions">
              <button class="kit-dialog-btn secondary" data-action="continue">
                Continuar Assim Mesmo
              </button>
              <button class="kit-dialog-btn primary" data-action="reset">
                Reiniciar Seleções
              </button>
            </div>
          </div>
        </div>
      `;

      document.body.appendChild(dialog);

      // Attach event handlers
      const closeBtn = dialog.querySelector('.kit-dialog-close');
      const continueBtn = dialog.querySelector('[data-action="continue"]');
      const resetBtn = dialog.querySelector('[data-action="reset"]');

      closeBtn?.addEventListener('click', () => dialog.remove());
      continueBtn?.addEventListener('click', () => dialog.remove());
      resetBtn?.addEventListener('click', () => {
        this.resetSelections('FULL');
        dialog.remove();
      });

      // Show dialog with animation
      setTimeout(() => dialog.classList.add('show'), 10);
    }

    // Navigate to next available slide, skipping blocked ones
    navigateToNextAvailableSlide() {
      const totalSlides = this.swiper.slides.length;
      let targetSlide = this.navigationController.currentSlideIndex + 1;

      // Find next slide with available options
      while (targetSlide < totalSlides && !this.hasAvailableOptions(targetSlide)) {
        console.log(`[NAV-DEBUG] Skipping slide ${targetSlide} - no available options`);
        targetSlide++;
      }

      if (targetSlide < totalSlides) {
        this.navigationController.goToSlide(targetSlide);
        if (targetSlide > this.navigationController.currentSlideIndex + 1) {
          this.uiManager.showNotification(
            `Pulando itens indisponíveis. Item ${targetSlide + 1} de ${totalSlides}.`,
            'info'
          );
        }
      } else {
        // No more slides with options - check if kit can be completed
        const feasibility = this.analyzeKitFeasibility();
        if (!feasibility.canComplete) {
          this.showKitBlockedDialog(feasibility);
        }
      }
    }

    // Reset selections with different strategies
    resetSelections(strategy = 'FULL') {
      switch (strategy) {
        case 'FULL':
          // Clear all selections and go to first slide
          for (let i = 0; i < this.swiper.slides.length; i++) {
            this.state.clearSelection(i);
          }
          this.navigationController.goToSlide(0, true);
          break;

        case 'FROM_CURRENT':
          // Clear selections from current slide onwards
          const currentSlide = this.navigationController.currentSlideIndex;
          for (let i = currentSlide; i < this.swiper.slides.length; i++) {
            this.state.clearSelection(i);
          }
          break;

        case 'INVALID_ONLY':
          // Clear only invalid selections
          for (let i = 0; i < this.swiper.slides.length; i++) {
            const selection = this.state.getSelection(i);
            if (selection && !this.isSelectionValid(this.swiper.slides[i], selection)) {
              this.state.clearSelection(i);
            }
          }
          break;
      }

      this.updateUI();
      this.uiManager.showNotification('Seleções resetadas com sucesso', 'success');
    }
  }

  // Initialize when ready
  function initializeKitSelectors() {
    const kitWrappers = document.querySelectorAll('.kit-selector-wrapper');

    kitWrappers.forEach(wrapper => {
      if (!wrapper.dataset.kitInitialized) {
        new KitSelector(wrapper);
        wrapper.dataset.kitInitialized = 'true';
      }
    });
  }

  function waitForSwiper(callback) {
    if (typeof Swiper !== 'undefined') {
      callback();
    } else {
      setTimeout(() => waitForSwiper(callback), 100);
    }
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => waitForSwiper(initializeKitSelectors));
  } else {
    waitForSwiper(initializeKitSelectors);
  }

  document.addEventListener('shopify:section:load', () => waitForSwiper(initializeKitSelectors));

})();